import { ReactElement, useCallback, useEffect, useState, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Bell } from 'lucide-react';
import { Toggle } from '@/components/Toggle';
import Select from '@/components/Select';
import { apiGetRequest, apiPostRequest, apiPutRequest } from '@/utils/apiClient';
import { fetchCalendarOauthReadiness, redirectToCalendarReauth } from '@/utils/calendarOauthReadiness';
import CalendarReauthConfirmModal from '@/features/scheduledReceipt/CalendarReauthConfirmModal';
import { useUser } from '@/hooks/UserContext';
import { useNextDeliveryDateTime } from '@/hooks/useNextDeliveryDateTime';
import { getWeekday } from '@/utils/time';
import { convert24To12, convert12To24, TIME_FORMAT_REGEX } from '@/utils/deliveryTime';

interface ReminderSettingsData {
    enabled: boolean;
    frequency: 'WEEKLY' | 'MONTHLY';
    localTime: string; // HH:mm format
    recipientEmail?: string; // 配信先メールアドレス
}

const ReminderSettings = (): ReactElement => {
    const { userInfo } = useUser();
    const { t, i18n } = useTranslation('scheduledReceipt');

    const [enabled, setEnabled] = useState<boolean>(false);
    const [frequency, setFrequency] = useState<'WEEKLY' | 'MONTHLY'>('WEEKLY');
    const [localTime, setLocalTime] = useState<string>('20:00');
    const [recipientEmail, setRecipientEmail] = useState<string>('');
    
    // am/pm表記用の状態（内部では24時間形式で保持）
    const [hour, setHour] = useState<number>(8); // 1-12
    const [isAm, setIsAm] = useState<boolean>(false); // true: am, false: pm
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [isSaving, setIsSaving] = useState<boolean>(false);
    const [error, setError] = useState<string>('');
    const [success, setSuccess] = useState<string>('');
    const [showEnabledMessage, setShowEnabledMessage] = useState<boolean>(false);
    const [showDisabledMessage, setShowDisabledMessage] = useState<boolean>(false);
    const [isOAuthReadinessChecking, setIsOAuthReadinessChecking] = useState<boolean>(false);
    const [calendarReauthModalOpen, setCalendarReauthModalOpen] = useState<boolean>(false);
    const [pendingReauthorizePath, setPendingReauthorizePath] = useState<string | null>(null);
    const [isTestSending, setIsTestSending] = useState<boolean>(false);
    const [testSendShowSent, setTestSendShowSent] = useState<boolean>(false);
    const [testSendError, setTestSendError] = useState<string>('');
    /** null: 未取得。0 でテスト送信ボタンを無効化し上限メッセージを表示 */
    const [testSendRemaining, setTestSendRemaining] = useState<number | null>(null);
    const testSendFeedbackTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    /** API からの初期同期では true にしない（表示直後に save が走らないようにする） */
    const shouldPersistEnabledRef = useRef<boolean>(false);

    useEffect(() => {
        return () => {
            if (testSendFeedbackTimerRef.current) {
                clearTimeout(testSendFeedbackTimerRef.current);
            }
        };
    }, []);

    // 保存前の状態を保持（失敗時に元に戻すため）
    const [prevEnabled, setPrevEnabled] = useState<boolean>(false);
    const [prevFrequency, setPrevFrequency] = useState<'WEEKLY' | 'MONTHLY'>('WEEKLY');
    const [prevLocalTime, setPrevLocalTime] = useState<string>('20:00');
    const [prevRecipientEmail, setPrevRecipientEmail] = useState<string>('');

    // タイムゾーン表示用
    const timezone = userInfo?.timeZone || 'UTC';

    // 初期データ取得
    useEffect(() => {
        const fetchSettings = async () => {
            setIsLoading(true);
            setError('');
            setTestSendError('');
            try {
                const response = await apiGetRequest('/api/delivery/settings');
                if (!response.ok) {
                    if (response.status === 404) {
                        // 設定が存在しない場合はデフォルト値を使用
                        setEnabled(false);
                        setFrequency('WEEKLY');
                        const defaultTime = '20:00';
                        setLocalTime(defaultTime);
                        const { hour: defaultHour, isAm: defaultIsAm } = convert24To12(defaultTime);
                        setHour(defaultHour);
                        setIsAm(defaultIsAm);
                        // デフォルトのメールアドレスは現在のユーザーのメールアドレス
                        setRecipientEmail(userInfo?.email || '');
                        return;
                    }
                    throw new Error('Failed to fetch settings');
                }
                const data: ReminderSettingsData = await response.json();
                setEnabled(data.enabled);
                setFrequency(data.frequency);
                const initialTime = data.localTime || '20:00';
                setLocalTime(initialTime);
                // 24時間形式をam/pm形式に変換
                const { hour: initialHour, isAm: initialIsAm } = convert24To12(initialTime);
                setHour(initialHour);
                setIsAm(initialIsAm);
                // 配信先メールアドレス（デフォルトは現在のユーザーのメールアドレス）
                const initialEmail = data.recipientEmail || userInfo?.email || '';
                setRecipientEmail(initialEmail);
                // 初期値を保存前の状態として保持
                setPrevEnabled(data.enabled);
                setPrevFrequency(data.frequency);
                setPrevLocalTime(initialTime);
                setPrevRecipientEmail(initialEmail);
            } catch (err) {
                console.error('Failed to fetch auto delivery settings:', err);
                setError(t('reminder.error'));
            } finally {
                setIsLoading(false);
            }
        };

        if (userInfo?.userId) {
            void fetchSettings();
        }
    }, [userInfo?.userId, t]);

    const refreshTestSendRemaining = useCallback(async () => {
        if (!userInfo?.userId) return;
        try {
            const response = await apiGetRequest('/api/delivery/test-send-count?type=reminder');
            if (!response.ok) return;
            const data: { remaining?: number } = await response.json();
            if (typeof data.remaining === 'number') {
                setTestSendRemaining(data.remaining);
            }
        } catch {
            /* 取得失敗時は既存の remaining を維持 */
        }
    }, [userInfo?.userId]);

    useEffect(() => {
        if (isLoading || !enabled || !userInfo?.userId) {
            if (!enabled) {
                setTestSendRemaining(null);
            }
            return;
        }
        void refreshTestSendRemaining();
    }, [isLoading, enabled, userInfo?.userId, refreshTestSendRemaining]);

    // 頻度オプション
    const frequencyOptions = useMemo(
        () => [
            { 
                value: 'WEEKLY', 
                label: t('reminder.frequency.weekly')
            },
            { 
                value: 'MONTHLY', 
                label: t('reminder.frequency.monthly')
            },
        ],
        [t],
    );

    const selectedFrequencyOption = useMemo(() => {
        return frequencyOptions.find((opt) => opt.value === frequency) || frequencyOptions[0];
    }, [frequency, frequencyOptions]);

    // 設定に変更があるか
    const hasChanges = useMemo(() => (
        enabled !== prevEnabled ||
        frequency !== prevFrequency ||
        localTime !== prevLocalTime ||
        recipientEmail !== prevRecipientEmail
    ), [enabled, prevEnabled, frequency, prevFrequency, localTime, prevLocalTime, recipientEmail, prevRecipientEmail]);

    const nextDeliveryDateTime = useNextDeliveryDateTime({ enabled, frequency, hour, isAm, timezone });

    // 設定保存
    const saveSettings = async () => {
        // 保存前の状態を記録
        setPrevEnabled(enabled);
        setPrevFrequency(frequency);
        setPrevLocalTime(localTime);
        setPrevRecipientEmail(recipientEmail);
        
        setIsSaving(true);
        setError('');
        setSuccess('');
        setTestSendError('');

        try {
            if (!TIME_FORMAT_REGEX.test(localTime)) {
                setError(t('reminder.invalidTimeFormat'));
                setIsSaving(false);
                return;
            }

            const response = await apiPutRequest('/api/delivery/settings', {
                enabled,
                frequency,
                localTime,
                recipientEmail,
            });

            if (!response.ok) {
                throw new Error('Failed to save settings');
            }

            // 保存成功時のメッセージ（有効/無効の変更時は専用メッセージ）
            if (enabled && !prevEnabled) {
                setSuccess('');
                setShowEnabledMessage(true);
                setShowDisabledMessage(false);
                setTimeout(() => setShowEnabledMessage(false), 5000);
            } else if (!enabled && prevEnabled) {
                setSuccess('');
                setShowEnabledMessage(false);
                setShowDisabledMessage(true);
                setTimeout(() => setShowDisabledMessage(false), 5000);
            } else {
                setShowEnabledMessage(false);
                setShowDisabledMessage(false);
                setSuccess(t('reminder.success'));
                setTimeout(() => setSuccess(''), 3000);
            }
        } catch (err) {
            console.error('Failed to save auto delivery settings:', err);
            // 保存失敗時は元の状態に戻す
            setEnabled(prevEnabled);
            setFrequency(prevFrequency);
            setLocalTime(prevLocalTime);
            setRecipientEmail(prevRecipientEmail);
            setError(t('reminder.errorNotSaved'));
        } finally {
            setIsSaving(false);
        }
    };

    const handleEnabledChange = async (next: boolean) => {
        if (!next) {
            setTestSendError('');
            setTestSendRemaining(null);
        }
        if (next) {
            setIsOAuthReadinessChecking(true);
            setError('');
            setTestSendError('');
            try {
                const data = await fetchCalendarOauthReadiness();
                if (!data.ready && data.reauthorizePath) {
                    setIsOAuthReadinessChecking(false);
                    setPendingReauthorizePath(data.reauthorizePath);
                    setCalendarReauthModalOpen(true);
                    return;
                }
            } catch (err) {
                setError(
                    err instanceof Error && err.message === 'CALENDAR_OAUTH_READINESS_NOT_FOUND'
                        ? t('reminder.oauthCheckEndpointNotFound')
                        : t('reminder.oauthCheckError'),
                );
                setIsOAuthReadinessChecking(false);
                return;
            }
            setIsOAuthReadinessChecking(false);
        }
        shouldPersistEnabledRef.current = true;
        setEnabled(next);
    };

    const sendTestEmail = async () => {
        setIsTestSending(true);
        setTestSendError('');
        setSuccess('');

        try {
            const response = await apiPostRequest('/api/delivery/settings/test-send', {
                enabled,
                frequency,
                localTime,
                recipientEmail,
            });

            if (!response.ok) {
                let message = t('reminder.sendTestError');
                try {
                    const errBody: { error?: string; code?: string } = await response.json();
                    if (errBody.code === 'TEST_SEND_DAILY_LIMIT') {
                        setTestSendRemaining(0);
                        message = t('reminder.sendTestLimitReached');
                    } else if (typeof errBody.error === 'string' && errBody.error.length > 0) {
                        message = errBody.error;
                    }
                } catch {
                    /* 本文が JSON でない場合はデフォルトメッセージ */
                }
                throw new Error(message);
            }

            const okBody: { remaining?: number } = await response.json();
            setTestSendError('');
            if (typeof okBody.remaining === 'number') {
                setTestSendRemaining(okBody.remaining);
            } else {
                void refreshTestSendRemaining();
            }
            setTestSendShowSent(true);
            if (testSendFeedbackTimerRef.current) {
                clearTimeout(testSendFeedbackTimerRef.current);
            }
            testSendFeedbackTimerRef.current = setTimeout(() => {
                setTestSendShowSent(false);
                testSendFeedbackTimerRef.current = null;
            }, 2500);
        } catch (err) {
            console.error('Failed to send reminder test email:', err);
            const msg = err instanceof Error ? err.message : t('reminder.sendTestError');
            if (msg === t('reminder.sendTestLimitReached')) {
                setTestSendError('');
            } else {
                setTestSendError(msg);
            }
        } finally {
            setIsTestSending(false);
        }
    };

    // enabled（トグル）をユーザーが変更したときだけ即時保存
    useEffect(() => {
        if (!isLoading && userInfo?.userId && shouldPersistEnabledRef.current) {
            shouldPersistEnabledRef.current = false;
            const timer = setTimeout(() => {
                void saveSettings();
            }, 500); // デバウンス
            return () => clearTimeout(timer);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [enabled]);

    // hourまたはisAmが変更されたらlocalTimeを更新
    useEffect(() => {
        if (!isLoading) {
            const newTime = convert12To24(hour, isAm);
            setLocalTime(newTime);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hour, isAm]);

    if (isLoading) {
        return (
            <section className="flex flex-col gap-3">
                <div className="animate-pulse">
                    <div className="h-6 bg-gray-200 rounded w-32 mb-2"></div>
                    <div className="h-4 bg-gray-200 rounded w-64 mb-4"></div>
                    <div className="h-10 bg-gray-200 rounded w-24"></div>
                </div>
            </section>
        );
    }

    const closeCalendarReauthModal = () => {
        setCalendarReauthModalOpen(false);
        setPendingReauthorizePath(null);
    };

    const confirmCalendarReauth = () => {
        if (pendingReauthorizePath) {
            redirectToCalendarReauth(pendingReauthorizePath);
        }
    };

    return (
        <section className="flex flex-col gap-4">
            <CalendarReauthConfirmModal
                isOpen={calendarReauthModalOpen}
                onClose={closeCalendarReauthModal}
                onConfirm={confirmCalendarReauth}
                variant="reminder"
            />
            {/* リマインダー有効化メッセージ */}
            {showEnabledMessage && enabled && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-sm text-green-800">
                    {t('reminder.enabledMessage')}
                </div>
            )}

            {/* リマインダー無効化メッセージ */}
            {showDisabledMessage && !enabled && (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-sm text-gray-800">
                    {t('reminder.disabledMessage')}
                </div>
            )}

            {/* エラーメッセージ */}
            {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-800">
                    {error}
                </div>
            )}

            {/* 成功メッセージ */}
            {success && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-sm text-green-800">
                    {success}
                </div>
            )}

            {/* 保存中表示（テスト送信の進捗はボタン文言で表示） */}
            {isSaving && (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-sm text-gray-800">
                    {t('reminder.saving')}
                </div>
            )}

            {/* 見出しとトグル */}
            <div className="flex items-start gap-3 pb-2 min-w-0">
                <Bell className="w-10 h-10 sm:w-11 sm:h-11 text-gray-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3">
                        <h3 className="text-lg font-medium">{t('reminder.heading')}</h3>
                        <Toggle
                            checked={enabled}
                            onChange={(next) => void handleEnabledChange(next)}
                            isDisabled={isOAuthReadinessChecking}
                            className="scale-75 origin-left"
                        />
                    </div>
                    <p className="text-sm text-gray-600 mt-1">{t('reminder.description')}</p>
                </div>
            </div>

            {/* 設定項目と次回配信日時 */}
            <div className="flex flex-col lg:flex-row lg:gap-8 items-stretch gap-6 min-w-0">
                {/* 左側：設定項目 */}
                <div className="flex gap-3 sm:gap-4 w-full max-w-xl lg:w-96 lg:max-w-none lg:shrink-0 min-w-0">
                    {/* ステップインジケーター */}
                    <div className="flex flex-col items-center pt-1">
                        {/* ステップ1 */}
                        <div className="flex flex-col items-center">
                            <div className="flex items-center justify-center w-7 h-7 rounded-full bg-gray-500 text-white text-xs font-bold flex-shrink-0">1</div>
                            <div className="w-0.5 flex-1 bg-gray-300 min-h-[6rem]"></div>
                        </div>
                        {/* ステップ2 */}
                        <div className="flex flex-col items-center">
                            <div className="flex items-center justify-center w-7 h-7 rounded-full bg-gray-500 text-white text-xs font-bold flex-shrink-0">2</div>
                            <div className="w-0.5 flex-1 bg-gray-300 min-h-[6rem]"></div>
                        </div>
                        {/* ステップ3 */}
                        <div className="flex flex-col items-center">
                            <div className="flex items-center justify-center w-7 h-7 rounded-full bg-gray-500 text-white text-xs font-bold flex-shrink-0">3</div>
                            <div className="w-0.5 flex-1 bg-gray-300 min-h-[6rem]"></div>
                        </div>
                        {/* ステップ4 */}
                        <div className="flex flex-col items-center">
                            <div className="flex items-center justify-center w-7 h-7 rounded-full bg-gray-500 text-white text-xs font-bold flex-shrink-0">4</div>
                        </div>
                    </div>

                    {/* ステップ内容 */}
                    <div className="flex flex-col gap-6 flex-1">
                        {/* 配信頻度 */}
                        <div className="flex flex-col gap-2 min-h-[6rem] justify-center">
                            <label className="text-sm font-medium">
                                {t('reminder.frequency.label')}
                            </label>
                            <div>
                                <Select
                                    options={frequencyOptions}
                                    defaultValue={selectedFrequencyOption}
                                    onValueChange={(option) => setFrequency(option.value as 'WEEKLY' | 'MONTHLY')}
                                    disabled={!enabled}
                                />
                            </div>
                        </div>

                        {/* 配信時間 */}
                        <div className="flex flex-col gap-2 min-h-[6rem] justify-center">
                            <label className="text-sm font-medium">
                                {t('reminder.time.label')}
                            </label>
                            <p className="text-xs text-gray-500">
                                {t('reminder.time.description', { timezone })}
                            </p>
                            <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                                {/* 午前/午後トグルボタン */}
                                <div className={`flex rounded-lg overflow-hidden border-2 shrink-0 ${!enabled ? 'border-gray-300' : 'border-lightest-gray'}`}>
                                    <button
                                        type="button"
                                        onClick={() => setIsAm(true)}
                                        disabled={!enabled}
                                        className={`px-3 sm:px-4 py-2 text-sm font-medium transition-colors ${
                                            !enabled
                                                ? 'bg-gray-100 text-gray-500 cursor-not-allowed'
                                                : isAm
                                                ? 'bg-gray-400 text-white'
                                                : 'bg-white text-gray-700 hover:bg-gray-50'
                                        }`}
                                    >
                                        {t('reminder.time.am')}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setIsAm(false)}
                                        disabled={!enabled}
                                        className={`px-3 sm:px-4 py-2 text-sm font-medium transition-colors border-l-2 ${
                                            !enabled
                                                ? 'bg-gray-100 text-gray-500 cursor-not-allowed border-gray-300'
                                                : !isAm
                                                ? 'bg-gray-400 text-white border-lightest-gray'
                                                : 'bg-white text-gray-700 hover:bg-gray-50 border-lightest-gray'
                                        }`}
                                    >
                                        {t('reminder.time.pm')}
                                    </button>
                                </div>
                                {/* 時間選択 */}
                                <select
                                    value={hour}
                                    onChange={(e) => setHour(Number(e.target.value))}
                                    className={`border-2 rounded-lg py-2 px-3 text-sm focus:border-kotomilThemaColor focus:outline-none focus:ring-2 focus:ring-kotomilThemaColor/20 ${
                                        !enabled
                                            ? 'bg-gray-100 text-gray-500 border-gray-300 cursor-not-allowed'
                                            : 'border-lightest-gray'
                                    }`}
                                    disabled={!enabled}
                                >
                                    {Array.from({ length: 12 }, (_, i) => i + 1).map((h) => (
                                        <option key={h} value={h}>
                                            {h}
                                        </option>
                                    ))}
                                </select>
                                {/* 時のテキスト */}
                                <span className="text-sm text-gray-700">{t('reminder.time.hour')}</span>
                            </div>
                        </div>

                        {/* 配信先 */}
                        <div className="flex flex-col gap-2 min-h-[6rem] justify-center">
                            <label className="text-sm font-medium">
                                {t('reminder.recipient.label')}
                            </label>
                            <p className="text-xs text-gray-500">
                                {t('reminder.recipient.description')}
                            </p>
                            <div>
                                <input
                                    type="text"
                                    value={recipientEmail || userInfo?.email || ''}
                                    readOnly
                                    className={`border-2 rounded-lg py-2 px-3 text-sm w-full cursor-not-allowed focus:border-kotomilThemaColor focus:outline-none focus:ring-2 focus:ring-kotomilThemaColor/20 ${
                                        !enabled
                                            ? 'bg-gray-100 text-gray-500 border-gray-300'
                                            : 'bg-gray-100 border-lightest-gray'
                                    }`}
                                />
                            </div>
                        </div>

                        {/* 更新ボタン */}
                        <div className="flex flex-col gap-3 min-h-[6rem] justify-center">
                            <button
                                type="button"
                                onClick={() => void saveSettings()}
                                disabled={isSaving || !hasChanges || !enabled}
                                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors border-2 w-full sm:w-32 max-w-xs ${
                                    isSaving || !hasChanges || !enabled
                                        ? 'bg-gray-200 border-gray-300 text-gray-500 cursor-not-allowed'
                                        : 'bg-kotomilThemaColor hover:bg-opacity-90 text-black border-kotomilThemaColor'
                                }`}
                            >
                                {isSaving ? t('reminder.saving') : t('reminder.updateButton')}
                            </button>
                        </div>
                    </div>
                </div>

                {/* 右側：次回配信日時（有効時のみ表示） */}
                {enabled && (
                    <div className="w-full lg:w-96 lg:flex-shrink-0 flex flex-col gap-4 bg-gray-100 p-4 sm:p-6 lg:p-8 rounded-xl lg:rounded-none min-w-0">
                        <div className="flex flex-col w-full">
                            <div className="rounded-lg p-4 flex flex-col w-full bg-white">
                                <h4 className="text-sm font-medium text-gray-700 mb-3 pb-3 border-b border-gray-300">
                                    {t('reminder.nextDelivery.label')}
                                </h4>
                                {nextDeliveryDateTime ? (
                                    <div className="space-y-2">
                                        <div className="text-base sm:text-lg font-semibold text-gray-900 break-words">
                                            {i18n.language?.startsWith('ja')
                                                ? nextDeliveryDateTime.format('YYYY年MM月DD日')
                                                : nextDeliveryDateTime.format('MMMM DD, YYYY')}
                                            <span className="block sm:inline sm:ml-2 text-sm sm:text-base font-normal text-gray-700 mt-1 sm:mt-0">
                                                ({getWeekday(nextDeliveryDateTime.format('YYYY-MM-DD'), i18n.language || 'en')})
                                            </span>
                                        </div>
                                        <div className="text-sm text-gray-600">
                                            {i18n.language?.startsWith('ja') 
                                                ? `${isAm ? t('reminder.time.am') : t('reminder.time.pm')}${hour}${t('reminder.time.hour')}`
                                                : `${hour} ${isAm ? t('reminder.time.am') : t('reminder.time.pm')}`
                                            }
                                        </div>
                                    </div>
                                ) : (
                                    <div className="text-sm text-gray-500">
                                        {t('reminder.nextDelivery.disabled')}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* テスト送信ボタン */}
                        <div className="flex flex-col gap-2 mt-2">
                            <div className="flex justify-center items-center gap-2">
                                <button
                                    type="button"
                                    onClick={() => void sendTestEmail()}
                                    disabled={
                                        isTestSending ||
                                        testSendShowSent ||
                                        testSendRemaining === null ||
                                        testSendRemaining <= 0
                                    }
                                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors border-2 w-full max-w-xs sm:w-48 flex items-center justify-center gap-2 ${
                                        isTestSending ||
                                        testSendShowSent ||
                                        testSendRemaining === null ||
                                        testSendRemaining <= 0
                                            ? testSendShowSent
                                                ? 'bg-green-50 border-green-500 text-green-800 cursor-default'
                                                : 'bg-gray-200 border-gray-300 text-gray-500 cursor-not-allowed'
                                            : 'bg-white hover:bg-gray-50 text-gray-800 border-kotomilThemaColor shadow-sm'
                                    }`}
                                >
                                    <span>
                                        {isTestSending
                                            ? t('reminder.sendTestSending')
                                            : testSendShowSent
                                              ? t('reminder.sendTestSentButton')
                                              : t('reminder.sendTestButton')}
                                    </span>
                                </button>
                            </div>
                            <p className="text-xs text-gray-500 text-center px-2">
                                {t('reminder.sendTestTooltip')}
                            </p>
                            {testSendRemaining !== null && testSendRemaining <= 0 ? (
                                <p
                                    className="text-sm text-amber-900 text-center px-2 mt-1"
                                    role="status"
                                >
                                    {t('reminder.sendTestLimitReached')}
                                </p>
                            ) : null}
                            {testSendError ? (
                                <p
                                    className="text-sm text-red-700 text-center px-2 mt-1"
                                    role="alert"
                                >
                                    {testSendError}
                                </p>
                            ) : null}
                        </div>
                    </div>
                )}
            </div>

        </section>
    );
};

export default ReminderSettings;

